import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  CreatePaymentAccountPayload,
  CreatePaymentAccountResponse,
  FioTransactionListResponse,
  PaymentAccountListResponse,
  RotateTokenPayload,
  RotateTokenResponse,
  SyncPaymentAccountResponse,
  UpdatePaymentAccountPayload,
} from "../schemas/paymentAccount";

/**
 * UC-007 multi-account FIO admin (DEC-006) — query + mutations.
 *
 * Backend (Phase 18):
 *   - GET  /api/admin/payment-accounts                  → PaymentAccountListResponse
 *   - POST /api/admin/payment-accounts                  → CreatePaymentAccountResponse
 *   - PUT  /api/admin/payment-accounts/{id}/rotate-token → RotateTokenResponse
 *   - POST /api/admin/payment-accounts/{id}/sync         → SyncPaymentAccountResponse
 */

const KEY_LIST = ["admin", "payment-accounts", "list"] as const;

export function usePaymentAccountList() {
  return useQuery({
    queryKey: KEY_LIST,
    queryFn: () =>
      apiCall<PaymentAccountListResponse>("/api/admin/payment-accounts"),
  });
}

export function useCreatePaymentAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePaymentAccountPayload) =>
      apiCall<CreatePaymentAccountResponse>("/api/admin/payment-accounts", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useUpdatePaymentAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdatePaymentAccountPayload) =>
      apiCall<{ account_id: string }>(
        `/api/admin/payment-accounts/${encodeURIComponent(accountId)}`,
        { method: "PUT", body: JSON.stringify(payload) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useRotatePaymentAccountToken(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RotateTokenPayload) =>
      apiCall<RotateTokenResponse>(
        `/api/admin/payment-accounts/${encodeURIComponent(accountId)}/rotate-token`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

/**
 * Stažené FIO transakce účtu — GET /api/admin/payment-accounts/{id}/transactions.
 * `enabled` gate: fetch až po otevření drill-down panelu.
 */
export function useFioTransactions(accountId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "payment-accounts", "transactions", accountId] as const,
    enabled,
    queryFn: () =>
      apiCall<FioTransactionListResponse>(
        `/api/admin/payment-accounts/${encodeURIComponent(accountId)}/transactions`,
      ),
  });
}

export function useSyncPaymentAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiCall<SyncPaymentAccountResponse>(
        `/api/admin/payment-accounts/${encodeURIComponent(accountId)}/sync`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}
